import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Code2 } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { ApiError } from '../../../api/client';
import { useAuth } from '../../../auth/useAuth';
import { ProcessingShowcase } from '../../../components/flow/ProcessingShowcase';
import { documentStep } from '../../../components/flow/stageToStep';
import { JsonViewerModal, type JsonViewerTab } from '../../../components/JsonViewerModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { Tabs } from '../../../components/Tabs';
import {
  fetchDocumentBlobObjectUrl,
  getDocumentRawAiResponse,
  type DocumentJobStatus,
  type DocumentResultResponse,
} from '../api';
import { AssigneeControl } from '../components/AssigneeControl';
import { AuditTrailPanel } from '../components/AuditTrailPanel';
import { ClassificationBadge } from '../components/ClassificationBadge';
import { ClassificationCorrectionControl } from '../components/ClassificationCorrectionControl';
import { ConfirmClassificationButton } from '../components/ConfirmClassificationButton';
import { CommentsPanel } from '../components/CommentsPanel';
import { DocumentStatsHud } from '../components/DocumentStatsHud';
import { DocumentMetaChips } from '../components/DocumentMetaChips';
import { ProcessingModelsBadge } from '../components/ProcessingModelsBadge';
import { FieldPanel } from '../components/FieldPanel';
import { PdfViewer } from '../components/PdfViewer';
import { ReValidateButton } from '../components/ReValidateButton';
import { SegmentChip } from '../components/SegmentChip';
import { SplitChildrenPanel } from '../components/SplitChildrenPanel';
import { StatusBanner } from '../components/StatusBanner';
import { useAuditTrail } from '../hooks/useAuditTrail';
import { useDocumentComments } from '../hooks/useDocumentComments';
import { useDocumentChildren } from '../hooks/useDocumentChildren';
import { reviewerQueryKeys, useDocumentResult } from '../hooks/useDocumentResult';
import { isTerminalStatus, useDocumentStatus } from '../hooks/useDocumentStatus';
import { useFieldEdit } from '../hooks/useFieldEdit';

const TERMINAL_REVIEW_STATUSES: ReadonlyArray<DocumentJobStatus> = ['review_required'];

/**
 * Visor de revisión (Phase 5 Wave 3). Layout dos columnas (60/40):
 *   - Izquierda: PdfViewer + BoundingBoxOverlay (SVG) sobre `sample.pdf` placeholder.
 *   - Derecha: StatusBanner + FieldPanel (filas editables) + ReValidateButton + audit-trail toggle.
 *
 * Sincronización: hover/click sobre un campo en la derecha resalta la bounding-box
 * a la izquierda; click sobre la box hace scroll a la fila correspondiente en la derecha.
 *
 * Botones disabled (Phase 6 backend gaps):
 *   - PATCH /fields/{name}: el commit es optimistic + log local; tooltip explica.
 *   - POST /revalidate: botón disabled con tooltip.
 *   - GET /audit-trail: panel abre vacío con copy informativo.
 *   - GET /blob: usa /sample.pdf hasta que exista el endpoint real.
 */
export function DocumentReviewerPage(): JSX.Element {
  const { id: rawId } = useParams<{ id: string }>();
  const jobId = rawId ?? '';

  const status = useDocumentStatus(jobId);
  const ready = isTerminalStatus(status.data?.status);
  // A Split parent is a terminal container with no /result of its own — it fans out into children.
  const isSplit = status.data?.status === 'split';
  // Expired by retention: the blob is gone, so requesting it would 404. Skip the fetch and say so.
  const isExpired = status.data?.expired === true;

  const result = useDocumentResult(jobId, { enabled: ready && !isSplit });
  const childrenQuery = useDocumentChildren(jobId, { enabled: ready && isSplit });
  const fieldEdit = useFieldEdit();

  const [highlightedFieldName, setHighlightedFieldName] = useState<string | null>(null);
  // Mobile-only (below lg): which pane the tab bar shows. Desktop shows both.
  const [mobileTab, setMobileTab] = useState<'doc' | 'fields'>('doc');
  const [auditOpen, setAuditOpen] = useState<boolean>(false);
  const [commentsOpen, setCommentsOpen] = useState<boolean>(false);
  const [jsonModalOpen, setJsonModalOpen] = useState<boolean>(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  // Lazy: only fetch the audit trail when the panel is opened, then cache it.
  const auditTrail = useAuditTrail(jobId, { enabled: auditOpen });
  // Lazy: comments load only when the panel is opened.
  const comments = useDocumentComments(jobId, { enabled: commentsOpen });

  const { hasScope, session } = useAuth();
  const canSeeRawJson = hasScope('platform:debug');

  // Lazy-fetch the raw AI JSON only when the modal is open AND the user has the debug scope.
  // 403 means the principal lacks the scope — surface as errorMessage in the tab, not a thrown error.
  const rawAiQuery = useQuery({
    queryKey: reviewerQueryKeys.rawAi(jobId),
    queryFn: ({ signal }) => getDocumentRawAiResponse(jobId, signal),
    enabled: jsonModalOpen && canSeeRawJson && jobId !== '',
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.detail.kind === 'forbidden') {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 60_000,
  });

  // Fetch the original document blob with the Bearer token and turn it into an object URL.
  // The URL is revoked on unmount and whenever the jobId changes so we don't leak memory
  // (each Blob in browser memory until it's revoked).
  useEffect(() => {
    if (jobId === '' || !ready || isSplit || isExpired) {
      return;
    }
    let cancelled = false;
    let activeUrl: string | null = null;
    void (async () => {
      try {
        const url = await fetchDocumentBlobObjectUrl(jobId);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        activeUrl = url;
        setBlobUrl(url);
      } catch (cause) {
        if (!cancelled) {
          console.error('Failed to load document blob', cause);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (activeUrl !== null) {
        URL.revokeObjectURL(activeUrl);
      }
    };
  }, [jobId, ready, isSplit, isExpired]);

  if (jobId === '') {
    return (
      <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        Identificador de documento inválido.
      </div>
    );
  }

  if (status.isError) {
    return <ErrorBanner error={status.error} jobId={jobId} />;
  }

  // Until the status is known, show only a plain spinner — never the animation.
  // (Rendering the flow while the status is still loading made it "flash" for a
  // moment when opening an already-processed document.)
  if (status.isLoading || status.data === undefined) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  const liveStatus = status.data.status;
  const isInFlight = liveStatus === 'queued' || liveStatus === 'processing';
  if (isInFlight) {
    const currentStage = status.data.currentStage ?? null;
    // Show the extra «Clasificando» beat only for auto jobs — detectable once the classifier stamps
    // the block or while the stage is `classifying`. Manual uploads keep the 6-step rail exactly.
    const inFlightClassified =
      (status.data.classification ?? null) !== null || currentStage === 'classifying';
    return (
      <div className="space-y-4">
        <ProcessingShowcase
          variant="document"
          step={documentStep(liveStatus, currentStage, inFlightClassified)}
          classified={inFlightClassified}
        />
        <StatusBanner
          status={liveStatus}
          classified={inFlightClassified}
          {...(currentStage !== null ? { currentStage } : {})}
        />
      </div>
    );
  }

  if (isSplit) {
    return (
      <div className="space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Documento dividido
            </h1>
            <p className="mt-1 font-mono text-xs text-slate-500">{jobId}</p>
          </div>
          <Link to="/documents" className="btn-secondary">
            Volver al listado
          </Link>
        </header>
        <StatusBanner status="split" />
        {childrenQuery.isError ? (
          <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
            No se pudieron cargar los sub-documentos.
          </div>
        ) : (
          <SplitChildrenPanel
            items={childrenQuery.data?.children ?? []}
            isLoading={childrenQuery.isLoading}
          />
        )}
      </div>
    );
  }

  if (result.isError) {
    return <ErrorBanner error={result.error} jobId={jobId} />;
  }

  if (result.isLoading || result.data === undefined) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  const data = result.data;
  const confidence = data.confidence;
  const showRevalidate = TERMINAL_REVIEW_STATUSES.includes(data.status);
  // An auto-classified job the classifier could not resolve: it has no type and no extracted fields, so
  // the standard field/confidence layout would be empty. `"(unclassified)"` is the backend's stable
  // placeholder slug for a typeless job (shared by list + result). The only exit is to pick a type and
  // reprocess, so we render a dedicated panel instead of the two-column reviewer.
  const isUnresolved = data.status === 'review_required' && data.documentType === '(unclassified)';

  if (isUnresolved) {
    return (
      <div className="space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Documento sin clasificar
            </h1>
            <p className="mt-1 font-mono text-xs text-slate-500">{data.documentId}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setAuditOpen(true)} className="btn-secondary">
              Ver audit trail
            </button>
            <Link to="/documents" className="btn-secondary">
              Volver al listado
            </Link>
          </div>
        </header>

        <StatusBanner status={data.status} />

        <div className="card border-amber-300 bg-amber-50 p-5">
          <h2 className="text-base font-semibold text-amber-900">No se pudo determinar el tipo</h2>
          <p className="mt-1 text-sm text-amber-800">
            El clasificador automático no pudo elegir un tipo con seguridad para este documento. Elige el
            tipo correcto para procesarlo; se creará un trabajo nuevo con ese tipo.
          </p>
          <div className="mt-4">
            <ClassificationCorrectionControl jobId={jobId} mode="panel" />
          </div>
        </div>

        <section
          aria-label="Vista del documento"
          className="min-h-[24rem] overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm"
        >
          {isExpired ? (
            <ExpiredDocumentNotice />
          ) : blobUrl === null ? (
            <div className="flex h-96 items-center justify-center">
              <LoadingSpinner label="Cargando documento…" />
            </div>
          ) : (
            <PdfViewer url={blobUrl} blocks={null} />
          )}
        </section>

        <AuditTrailPanel
          open={auditOpen}
          onClose={() => setAuditOpen(false)}
          entries={auditTrail.data?.entries ?? []}
          routingTrace={auditTrail.data?.routingTrace ?? null}
          isLoading={auditTrail.isLoading}
        />
      </div>
    );
  }

  // A segment of a split upload shares the parent's full blob; the viewer is bounded to its
  // page range instead. Older children without a persisted range fall back to the whole PDF.
  const segment =
    typeof data.parentJobId === 'string' &&
    typeof data.startPage === 'number' &&
    typeof data.endPage === 'number'
      ? {
          parentJobId: data.parentJobId,
          segmentIndex: data.segmentIndex ?? null,
          segmentLabel: data.segmentLabel ?? null,
          startPage: data.startPage,
          endPage: data.endPage,
        }
      : null;

  return (
    // The whole reviewer fills the inner content box of <main> and never grows beyond it.
    // Children manage their own scroll surfaces — the global <main> scrollbar stays idle
    // on this route. min-h-0 on every flex parent is what lets the inner overflow-auto
    // actually clip the content instead of pushing the page taller.
    <div className="flex h-full min-h-0 flex-col gap-5 overflow-hidden">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {data.documentType}
            <span className="ml-2 text-sm font-normal text-slate-500">
              v{data.documentTypeVersion}
            </span>
          </h1>
          <p className="mt-1 font-mono text-xs text-slate-500">{data.documentId}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {segment !== null ? <SegmentChip {...segment} /> : null}
            <ProcessingModelsBadge models={data.processingModels} />
            <DocumentMetaChips metrics={data.metrics} />
            {data.classification?.wasAutoClassified === true ? (
              <ClassificationBadge classification={data.classification} />
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <AssigneeControl
            jobId={jobId}
            assignedTo={data.assignedTo ?? null}
            currentUserId={session?.userId ?? null}
          />
          <button
            type="button"
            onClick={() => setJsonModalOpen(true)}
            className="btn-secondary inline-flex items-center gap-1"
            data-testid="open-json-modal"
          >
            <Code2 className="h-4 w-4" aria-hidden strokeWidth={2} />
            Ver JSON
          </button>
          <button
            type="button"
            onClick={() => setAuditOpen(true)}
            className="btn-secondary"
          >
            Ver audit trail
          </button>
          <button
            type="button"
            onClick={() => setCommentsOpen(true)}
            className="btn-secondary"
            data-testid="open-comments"
          >
            Comentarios
          </button>
          <ReValidateButton visible={showRevalidate} jobId={jobId} />
          <Link to="/documents" className="btn-secondary">
            Volver al listado
          </Link>
        </div>
      </header>

      <StatusBanner
        status={data.status}
        {...(confidence?.triggerReason !== undefined && confidence.triggerReason !== ''
          ? { triggerReason: confidence.triggerReason }
          : {})}
      />

      {data.classification?.wasAutoClassified === true ? (
        <div className="flex flex-wrap items-center gap-3">
          <ConfirmClassificationButton
            jobId={jobId}
            classification={data.classification}
            status={data.status}
          />
          <ClassificationCorrectionControl
            jobId={jobId}
            mode="inline"
            currentSlug={data.documentType}
          />
        </div>
      ) : null}

      <DocumentStatsHud
        fields={confidence?.fields ?? []}
        edits={fieldEdit.edits}
        globalScore={confidence?.globalScore ?? null}
      />

      {/* Below lg the two panes don't fit — a tab bar swaps between them. Both
          stay MOUNTED (hidden, not unmounted) so the PDF canvas never reloads. */}
      <Tabs
        className="lg:hidden"
        tabs={[
          { id: 'doc', label: 'Documento' },
          { id: 'fields', label: 'Campos' },
        ]}
        active={mobileTab}
        onChange={(id) => setMobileTab(id as 'doc' | 'fields')}
      />

      <div className="grid min-h-0 flex-1 gap-5 overflow-hidden lg:grid-cols-[3fr,2fr]">
        <section
          aria-label="Vista del documento"
          className={clsx(
            mobileTab === 'doc' ? 'flex' : 'hidden',
            'min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm lg:flex',
          )}
        >
          {isExpired ? (
            <ExpiredDocumentNotice />
          ) : blobUrl === null ? (
            <div className="flex flex-1 items-center justify-center py-12">
              <LoadingSpinner label="Cargando documento…" />
            </div>
          ) : (
            <PdfViewer
              url={blobUrl}
              blocks={data.structuralBlocks ?? null}
              {...(segment !== null
                ? { startPage: segment.startPage, endPage: segment.endPage }
                : {})}
            />
          )}
        </section>
        <section
          aria-label="Campos extraídos"
          className={clsx(
            mobileTab === 'fields' ? 'flex' : 'hidden',
            'min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm lg:flex',
          )}
        >
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            <FieldPanel
              result={data}
              edits={fieldEdit.edits}
              highlightedFieldName={highlightedFieldName}
              onCommit={(name, value) => {
                void fieldEdit.commit(jobId, name, value);
              }}
              onFocusField={setHighlightedFieldName}
            />
          </div>
        </section>
      </div>

      <AuditTrailPanel
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        entries={auditTrail.data?.entries ?? []}
        routingTrace={auditTrail.data?.routingTrace ?? null}
        isLoading={auditTrail.isLoading}
      />

      <CommentsPanel
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        jobId={jobId}
        comments={comments.data ?? []}
        isLoading={comments.isLoading}
        currentUserId={session?.userId ?? null}
      />

      <JsonViewerModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        title="JSON del documento"
        subtitle={`Job ${data.documentId} · ${data.documentType} v${data.documentTypeVersion}`}
        tabs={buildJsonTabs({
          resultData: data,
          canSeeRaw: canSeeRawJson,
          rawIsLoading: rawAiQuery.isLoading,
          rawError: rawAiQuery.error,
          rawValue: rawAiQuery.data?.rawAiJson ?? null,
        })}
      />
    </div>
  );
}

interface BuildJsonTabsArgs {
  resultData: DocumentResultResponse;
  canSeeRaw: boolean;
  rawIsLoading: boolean;
  rawError: unknown;
  rawValue: string | null;
}

function buildJsonTabs({
  resultData,
  canSeeRaw,
  rawIsLoading,
  rawError,
  rawValue,
}: BuildJsonTabsArgs): JsonViewerTab[] {
  const tabs: JsonViewerTab[] = [
    {
      id: 'normalized',
      label: 'JSON',
      value: resultData,
    },
  ];
  if (!canSeeRaw) {
    return tabs;
  }
  const isForbidden = rawError instanceof ApiError && rawError.detail.kind === 'forbidden';
  if (isForbidden) {
    tabs.push({
      id: 'raw',
      label: 'JSON raw',
      value: null,
      errorMessage: 'Esta cuenta no tiene el scope platform:debug.',
    });
    return tabs;
  }
  const errMessage =
    rawError instanceof ApiError
      ? rawError.message
      : rawError instanceof Error
        ? rawError.message
        : rawError !== null && rawError !== undefined
          ? 'Error desconocido al obtener la respuesta cruda.'
          : undefined;
  tabs.push({
    id: 'raw',
    label: 'JSON raw',
    isLoading: rawIsLoading,
    ...(errMessage !== undefined ? { errorMessage: errMessage } : {}),
    value:
      rawValue ??
      '(El job no tiene respuesta cruda persistida — procesado antes de Wave 6.D, o vino vacío.)',
  });
  return tabs;
}

/**
 * Shown in the document pane instead of the viewer once a job has expired. Without it the pane would
 * spin on "Cargando documento…" forever (the blob no longer exists), which reads as a broken page rather
 * than as the retention policy doing its job.
 */
function ExpiredDocumentNotice(): JSX.Element {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <strong className="text-sm font-semibold text-slate-700">Documento caducado</strong>
      <p className="max-w-md text-sm text-slate-500">
        Ha superado el periodo de retención de la licencia. El archivo original y los datos extraídos se
        han eliminado de forma permanente; solo se conservan el estado, las puntuaciones y la auditoría.
      </p>
    </div>
  );
}

function ErrorBanner({ error, jobId }: { error: unknown; jobId: string }): JSX.Element {
  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        <strong className="font-semibold">Documento no encontrado en este tenant.</strong>
        <p className="mt-1">
          El job <span className="font-mono">{jobId}</span> no existe o ha sido eliminado.
        </p>
        <Link to="/documents" className="mt-3 inline-flex underline">
          Volver al listado
        </Link>
      </div>
    );
  }

  const message = error instanceof Error ? error.message : 'Error desconocido.';
  return (
    <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
      <strong className="font-semibold">No se pudo cargar el documento.</strong>
      <p className="mt-1">{message}</p>
    </div>
  );
}
